<template>
  <div
    class="container"
    :class="{
      'pill-tab-small': tabStyle === 'pillTabSmall',
      container__small: size === 'small'
    }"
  >
    <div
      v-for="(button, index) in buttons"
      :key="index"
      :aria-pressed="selectedName === button.name"
      class="button"
      :button-text="button.text"
      :class="{ button__small: size === 'small', button__selected: selectedName === button.name }"
      role="button"
      tabindex="0"
      @click="handleButtonClick(button.name)"
      @keydown.enter="handleButtonClick(button.name)"
      @keydown.space="(event: Event) => handleButtonClick(button.name, event, true)"
    >
      {{ button.text }}
    </div>
  </div>
</template>

<script setup lang="ts">
interface Button {
  text: string
  name: string
  onClick: () => void
}

const props = defineProps<{
  buttons: Button[]
  selectedName?: string
  size?: string
  tabStyle?: string
}>()

const emits = defineEmits<{
  (e: 'buttonClicked', name: string): void
}>()

const handleButtonClick = (name: string, event?: Event, preventDefault?: boolean) => {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  emits('buttonClicked', name)
  props.buttons.find((b) => b.name === name)?.onClick()
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
.container {
  @include font-mixins.desktop-bold-h5;
  display: flex;
  flex-direction: row;
  gap: 32px;
  height: 64px;
  color: variables.$color-greyscale-content;
  width: fit-content;
  border-bottom: 1px solid variables.$color-greyscale-line-grey;
  &__small {
    @include font-mixins.desktop-bold-label;
    gap: 16px;
    height: 40px;
  }
}
.button {
  margin-bottom: 4px;
  padding: 16px 0;
  cursor: pointer;
  &__small {
    padding: 9px 0;
  }
  &:hover {
    color: variables.$color-primary;
    border-bottom: 4px solid variables.$color-primary;
    border-radius: 0;
    margin-bottom: 0;
  }
  &__selected {
    color: variables.$color-primary;
    border-bottom: 4px solid #3f46d6;
    border-radius: 0;
    margin-bottom: 0;
  }
}
.pill-tab-small {
  @include font-mixins.desktop-bold-paragraph;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: unset;
  color: variables.$color-greyscale-secondary;
  border-bottom: 0px;

  .button {
    display: flex;
    cursor: pointer;
    padding: 12px 16px;
    margin-bottom: 0px;
    border-bottom: 0px;
    border-radius: 4px;
    &__small {
      padding: 8px 12px;
    }

    &:hover {
      color: variables.$color-primary;
      background-color: variables.$color-primary-light;
      border-bottom: 0px;
      margin-bottom: 0px;
    }

    &__selected {
      color: variables.$color-primary;
      background-color: variables.$color-primary-light;
      border-bottom: 0px;
      margin-bottom: 0px;
    }
  }
}
</style>
